import {PricingOptions, Tabs} from '@primer/react-brand'
import {documentToReactComponents} from '@contentful/rich-text-react-renderer'
import {InfoIcon, SparkleFillIcon} from '@primer/octicons-react'
import type {ComponentType, ReactElement, ReactNode} from 'react'

import type {PricingFeature, PricingPlan, PricingOption} from '../../../types'
import {renderWithNoMarks} from '../../../utils'
import styles from './PricingSection.module.css'

type PricingOptionsFeatureListProps = {
  children: ReactNode
  expanded?: boolean
}

const PricingOptionsFeatureList = PricingOptions.FeatureList as unknown as ComponentType<PricingOptionsFeatureListProps>

export function PricingSectionPlans({options}: {options: PricingOption[]}) {
  const hasMultipleOptions = options.length > 1

  // When multiple pricing options exist (e.g. "For individuals" / "For businesses"),
  // render them as tabs so users can switch between plan groups
  if (hasMultipleOptions) {
    return (
      <Tabs aria-label="Pricing plans" variant="accent">
        {options.map(option => (
          <Tabs.Item key={option.sys.id}>{option.fields.label}</Tabs.Item>
        ))}
        {options.map(option => (
          <Tabs.Panel className={styles.tabs} key={option.sys.id}>
            {renderPlansForOption(option)}
          </Tabs.Panel>
        ))}
      </Tabs>
    )
  }

  // Single pricing option: render plans directly without tabs
  return renderPlansForOption(options[0]!)
}

function renderPlansForOption(option: PricingOption) {
  const {plans} = option.fields
  const hasFeatures = plans.some(plan => (plan.fields.featureList?.length ?? 0) > 0)
  const variant = hasFeatures ? 'default' : 'default-gradient'

  return <PricingOptions variant={variant}>{plans.map(plan => renderPlanItem(plan))}</PricingOptions>
}

function renderPlanItem(plan: PricingPlan) {
  const {
    heading,
    description,
    price,
    originalPrice,
    priceTrailing,
    actionMessage,
    primaryCta,
    secondaryCta,
    featureListTitle,
    highlight,
    featureList,
    trailingText,
  } = plan.fields

  const featureListItems = featureList?.map(feature => renderFeature(feature)) ?? []
  const featureListChildren: ReactElement[] = [
    ...(featureListTitle
      ? [
          (
            <PricingOptions.FeatureListHeading key={`${plan.sys.id}-feature-list-heading`}>
              {featureListTitle}
            </PricingOptions.FeatureListHeading>
          ) as unknown as ReactElement,
        ]
      : []),
    ...featureListItems,
  ]

  return (
    <PricingOptions.Item key={plan.sys.id}>
      <PricingOptions.Heading>{renderWithNoMarks(heading)}</PricingOptions.Heading>
      {highlight && <PricingOptions.Label>{highlight}</PricingOptions.Label>}
      <PricingOptions.Description>{renderWithNoMarks(description)}</PricingOptions.Description>
      <PricingOptions.Price originalPrice={originalPrice} trailingText={priceTrailing}>
        {price}
      </PricingOptions.Price>
      {actionMessage && (
        <PricingOptions.ActionsMessage className={styles.alertMessage} leadingComponent={<InfoIcon />}>
          {documentToReactComponents(actionMessage)}
        </PricingOptions.ActionsMessage>
      )}
      {primaryCta && (
        <PricingOptions.PrimaryAction as="a" href={primaryCta.fields.href} variant="primary">
          {primaryCta.fields.text}
        </PricingOptions.PrimaryAction>
      )}
      {secondaryCta && (
        <PricingOptions.SecondaryAction as="a" href={secondaryCta.fields.href}>
          {secondaryCta.fields.text}
        </PricingOptions.SecondaryAction>
      )}
      {featureListChildren.length > 0 && (
        <PricingOptionsFeatureList expanded>{featureListChildren}</PricingOptionsFeatureList>
      )}
      {trailingText && (
        <PricingOptions.Footnote
          className={!featureList || featureList.length === 0 ? styles.trailingTextSansFeatures : ''}
        >
          {documentToReactComponents(trailingText)}
        </PricingOptions.Footnote>
      )}
    </PricingOptions.Item>
  )
}

function renderFeature(feature: PricingFeature): ReactElement {
  const {title, text, variant} = feature.fields
  const resolvedVariant = variant ?? 'Included'

  if (resolvedVariant === 'Title') {
    return (
      <PricingOptions.FeatureListGroupHeading key={feature.sys.id}>
        {title ?? renderWithNoMarks(text)}
      </PricingOptions.FeatureListGroupHeading>
    )
  }

  if (resolvedVariant === 'Excluded') {
    return (
      <PricingOptions.FeatureListItem key={feature.sys.id} variant="excluded">
        {renderWithNoMarks(text)}
      </PricingOptions.FeatureListItem>
    )
  }

  if (resolvedVariant === 'Spark') {
    return (
      <PricingOptions.FeatureListItem
        key={feature.sys.id}
        leadingVisual={SparkleFillIcon}
        leadingVisualFill="var(--base-color-scale-purple-3)"
      >
        {renderWithNoMarks(text)}
      </PricingOptions.FeatureListItem>
    )
  }

  return <PricingOptions.FeatureListItem key={feature.sys.id}>{renderWithNoMarks(text)}</PricingOptions.FeatureListItem>
}
